import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { FieldTypeBadge } from '@/features/custom-fields/public';
import { CustomFieldPopoverForm } from './CustomFieldPopoverForm';
import type { TemplateCustomField } from '../types';
import type { CustomFieldPopoverFormValues } from '../schemas';

interface TemplateFieldsTabProps {
  fields: TemplateCustomField[];
  editable: boolean;
  onAdd?: (values: CustomFieldPopoverFormValues) => void;
  onRemove?: (index: number) => void;
  isPending?: boolean;
}

export function TemplateFieldsTab({
  fields,
  editable,
  onAdd,
  onRemove,
  isPending = false,
}: TemplateFieldsTabProps) {
  const { t } = useTranslation('projectTemplates');

  return (
    <div className="flex flex-col gap-2" data-testid="project-templates-fields-tab">
      {fields.length === 0 ? (
        <p
          className="py-4 text-center text-sm text-muted-foreground"
          data-testid="project-templates-fields-empty"
        >
          {t('preview.emptyStates.fields')}
        </p>
      ) : (
        fields.map((field, index) => (
          <div
            key={`${field.name}-${index}`}
            className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
            data-testid={`project-templates-field-row-${index}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm">{field.name}</span>
              <FieldTypeBadge fieldType={field.type} size="sm" />
            </div>
            {editable && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    disabled={isPending}
                    aria-label={t('actions.remove')}
                    onClick={() => onRemove?.(index)}
                    data-testid={`project-templates-field-remove-${index}`}
                  >
                    <X className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('actions.remove')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        ))
      )}

      {editable && onAdd && <CustomFieldPopoverForm onAdd={onAdd} isPending={isPending} />}
    </div>
  );
}
